/**
 * Auto-layout for nested boards + Module-tree adapter.
 *
 * Per-space layout runs `stressLayout` (see ./stressLayout.ts) — a
 * stress-majorisation solver that places connected nodes close, gently biases
 * them left→right along the directed edges, clusters by team/domain, and
 * honours external ports as phantom anchors on the space's wall. Explicit
 * hand-placements always win.
 */
import type { DependencyEdge, EdgeWidgetStyleData, Interface, Module, StableId } from "../arch-model/model.js";
import { getTeam, getDomain, edgeFromKey, edgeToKey, moduleKey } from "../arch-model/utils.js";
import { aggregateInterfaceWidget, extractWidgetSpec } from "../arch-model/widget-spec.js";
import { resolveNodeSize, widgetTagRowAnchor } from "./widgetSize.js";
import {
  type BoardEdge,
  type BoardItem,
  type BoardSpace,
  type Connection,
  type ExternalPort,
  type ExternalPortDetail,
  type PortDirection,
  type PlacedGroupFrame,
  type PlacementOverrides,
  connectionIdFor,
} from "./types.js";
import { FRAME_PAD, FRAME_LABEL_H } from "./layoutParams.js";
import { PORT_LANE, PORT_H, wallStackCentreExtent } from "./portGeom.js";
import {
  stressLayout,
  type Rect as StressRect,
  type StressEdge,
  type StressNode,
  type StressPort,
} from "./stressLayout.js";
import type { FlowDirection } from "./direction.js";

export interface ModuleWithParent extends Module {
  readonly parentId?: StableId;
}

/** `group by` frame spec (structural mirror of the projection's
 *  `GroupFrameSpec` — scene board stays decoupled from arch-model's
 *  projection module): board-id member lists for the root space. */
export interface GroupFrameSpec {
  readonly id: string;
  readonly label: string;
  /** DIRECT members only; descendants live in child specs via `parentId`. */
  readonly memberBoardIds: readonly string[];
  /** Nested `group by`: parent spec id (absent = top level). */
  readonly parentId?: string;
}

/** Group id → parent group id, from a spec list's parent links. */
function parentsOf(frames: readonly GroupFrameSpec[]): Map<string, string> | undefined {
  const m = new Map<string, string>();
  for (const f of frames) if (f.parentId !== undefined) m.set(f.id, f.parentId);
  return m.size > 0 ? m : undefined;
}

/**
 * Previous-layout snapshot keyed by item id, used as warm-start for the
 * next solve. Stable edits (adding/removing a single node) re-use neighbours'
 * positions; big changes still settle because the solver runs its full budget
 * regardless.
 */
export type PreviousLayout = ReadonlyMap<string, { x: number; y: number; w: number; h: number }>;

export interface LayoutContext {
  byParent: Map<string, ModuleWithParent[]>;
  edgesByParent: Map<string | undefined, BoardEdge[]>;
  externalPorts: Map<string, ExternalPort[]>;
  internalCalls: Map<string, ExternalPortDetail[]>;
  /** First-class cross-space connection index, keyed by `${sourceId}=>${targetId}`. */
  connections: Map<string, Connection>;
  placements: PlacementOverrides;
  previousLayout: PreviousLayout;
  dragging: boolean;
  /**
   * When set, root-space items use these positions verbatim and skip
   * the solver. Used by the animation driver: it runs the generator
   * externally, captures each intermediate result, and publishes it via
   * this map. Nested spaces solve normally.
   */
  rootPositionsOverride?: ReadonlyMap<string, StressRect>;
  /**
   * Screen direction of the dependency flow for every space (default "LR").
   * Passed straight through to the solver, which handles it as a boundary
   * transform — see stressLayout.ts / direction.ts.
   */
  direction?: FlowDirection;
  /**
   * Layout as of DRAG-GESTURE START (see StressInput.trimBase): standing
   * forces are absorbed against this baseline so the gesture's own force
   * deltas act in full. Set by the app while dragging.
   */
  trimBase?: ReadonlyMap<string, { x: number; y: number; w: number; h: number }>;
  /**
   * Model-edit baseline: the PREVIOUS solve's layout plus the topology it
   * was solved under. On idle recomputes, `placeSpace` compares each
   * space's current geometry (children, sizes, edges, external ports)
   * against this; a difference makes the recompute an EDIT GESTURE — the
   * pre-edit rects/topology flow to the solver (StressInput.trimBase +
   * editBaseline) so only the edit's own force deltas act, exactly like a
   * drag. Spaces that match the baseline keep the maintenance-hold path.
   * Ignored while dragging.
   */
  editBase?: {
    rects: PreviousLayout;
    structure: LayoutStructure;
    /** PRE-EDIT `group by` frames — the calibration world's forced
     *  partition (a membership move must read as the mover's own delta,
     *  not phantom forces on the untouched rest). */
    groupFrames?: readonly GroupFrameSpec[];
  };
  /**
   * The previous build's spaces, keyed by space id. During a drag we reuse
   * the exact prior `BoardItem` / `BoardSpace` objects for anything whose
   * bounds are unchanged this frame, so React's memo and the edge-route
   * memo (both keyed on object identity) skip the entire unchanged subtree
   * instead of reconciling + re-routing it every pointer frame. Only the
   * nodes the solver actually moved get fresh identities. Drag-only: never
   * set on the animation or idle paths, so it can't affect their output.
   */
  prevSpaceById?: Map<string, BoardSpace>;
  /**
   * Drag relaxation throttle (dogfood switch): when true on a drag frame,
   * every space — including the one whose pin moved — reuses its warm
   * layout verbatim (pins still win, so the dragged node tracks the
   * pointer). The caller alternates it per pointermove frame.
   */
  dragSolveSkip?: boolean;
  /**
   * `group by` frames (frame-groups) for the ROOT space: derives the
   * solver's forced partition and, post-solve, the rendered
   * `BoardSpace.groupFrames` rects. Absent on the default board and on
   * non-grouped views.
   */
  groupFrames?: readonly GroupFrameSpec[];
  /**
   * View BOUNDARY stubs (VIEWS §7), keyed by board node id: port-nubs for
   * calls whose far endpoint is absent from the view. Merged into each
   * item's `externalPorts` (and the solver's port set, so the stub exerts
   * the same wall-direction pull a real port does). Absent on the default
   * board.
   */
  boundaryPorts?: ReadonlyMap<string, readonly ExternalPort[]>;
}

/**
 * The placement-INDEPENDENT part of a `LayoutContext`: the module/edge topology
 * (index by parent, edges routed to their LCA space, external ports, internal
 * calls) plus the root set. Derived purely from `modules` + `edges` — it does
 * not read `placements`, `previousLayout`, drag state, or any solve output, so
 * it is identical whether the user is dragging, animating, or idle. That makes
 * it cacheable across those mode switches and shareable between a caller's
 * global connection index and the board engine (one O(N+E) build per edit, not
 * two). `prepareLayout` layers the cheap placement fields on top.
 */
export interface LayoutStructure {
  byParent: Map<string, ModuleWithParent[]>;
  edgesByParent: Map<string | undefined, BoardEdge[]>;
  externalPorts: Map<string, ExternalPort[]>;
  internalCalls: Map<string, ExternalPortDetail[]>;
  /** First-class cross-space connection index, keyed by `${sourceId}=>${targetId}`. */
  connections: Map<string, Connection>;
  roots: readonly ModuleWithParent[];
}

/**
 * Should a caller DROP its warm-start layout for the next solve? True when
 * the edge topology changed materially (>15%) since the layout being
 * reused was produced. A warm start from a materially different graph
 * (e.g. the model still streaming in at startup) makes every solve a
 * low-quality incremental patch on stale geometry — organization whipsaws
 * and never recovers. Single-edit deltas stay far below the threshold, so
 * normal editing keeps its warm stability.
 */
export function shouldDropWarmStart(
  prevEdgeCount: number,
  nextEdgeCount: number,
): boolean {
  return (
    Math.abs(nextEdgeCount - prevEdgeCount) / Math.max(1, prevEdgeCount) > 0.15
  );
}

/**
 * Solver budget + mode for one space, from its warm-start/pin coverage.
 * The SINGLE source of truth — `placeSpace` (sync path) and
 * `prepareRootStressInput` (animation path) both use it, so the two paths
 * cannot drift apart on when to run the global SGD phase or sand friction.
 *
 * - Dragging → gentle per-frame budget, sand friction.
 * - Warm start covering ≥70% of children → incremental (local relaxation);
 *   with ZERO cold children also sand — a same-topology recompute must keep
 *   the standing layout still instead of re-settling the whole graph.
 * - Otherwise (cold, or >30% new children after a bulk paste / big edit) →
 *   full solve: local relaxation cannot integrate that much new content, so
 *   the global search runs (warm nodes still seed it, pins still hold).
 */
export function chooseSolveParams(
  childKeys: readonly string[],
  previous: ReadonlyMap<string, unknown>,
  pinned: ReadonlyMap<string, unknown>,
  dragging: boolean,
): { budget: "drag" | "incremental" | "full"; mode: "float" | "sand" } {
  let coldCount = 0;
  for (const k of childKeys) {
    if (!previous.has(k) && !pinned.has(k)) coldCount++;
  }
  const hasAny = previous.size > 0;
  const warmEnough = hasAny && coldCount / Math.max(1, childKeys.length) <= 0.3;
  const budget = dragging ? "drag" : warmEnough ? "incremental" : "full";
  const mode = dragging || (warmEnough && coldCount === 0) ? "sand" : "float";
  return { budget, mode };
}

/**
 * Solve ONE space's direct children — no recursion into subspaces. Returns the
 * placed items (each flagged `hasSpace` when it owns a nested space, but with
 * `space` left undefined) plus the frame + edges. This is the unit the lazy
 * `BoardEngine` caches: it depends only on the space's own child set, their
 * intrinsic sizes, this space's edges, pins, and warm-start — never on anything
 * below. The eager `buildSpace` and the engine both build on it.
 */
/** Node id → frame id over the ids actually present in a space. */
function partitionOf(
  frames: readonly GroupFrameSpec[],
  idSet: ReadonlySet<string>,
): Map<string, string> {
  const m = new Map<string, string>();
  for (const f of frames) {
    for (const bid of f.memberBoardIds) if (idSet.has(bid)) m.set(bid, f.id);
  }
  return m;
}

/**
 * Place `group by` frames over solved positions — one fragment per
 * (frame × connected component), nested per the spec tree. Rects derive
 * BOTTOM-UP (the One-rect rule: bbox of direct-member rects ∪ child
 * fragment rects, + FRAME_PAD, + FRAME_LABEL_H on top — pads stack per
 * level, so a parent's label never underlaps a child frame). `components`
 * is the SOLVER's decomposition (StressResult.components — includes
 * port-peer unions, not re-derivable here); without it (animation
 * override / drag warm-reuse) fragment membership falls back to the
 * previous build's fragments, else one fragment per spec — transient
 * states only, the next real solve restores the exact split. A child
 * fragment binds to the parent FRAGMENT covering its CC. Emission order:
 * (depth asc, id) — the renderers' paint order.
 */
function placeGroupFrames(
  frames: readonly GroupFrameSpec[],
  positions: ReadonlyMap<string, StressRect>,
  components: ReadonlyMap<string, number> | undefined,
  prevFrames: readonly PlacedGroupFrame[] | undefined,
): PlacedGroupFrame[] {
  const specById = new Map(frames.map((f) => [f.id, f] as const));
  const childSpecs = new Map<string, GroupFrameSpec[]>();
  const rootSpecs: GroupFrameSpec[] = [];
  for (const f of frames) {
    if (f.parentId !== undefined && specById.has(f.parentId)) {
      const arr = childSpecs.get(f.parentId) ?? [];
      arr.push(f);
      childSpecs.set(f.parentId, arr);
    } else {
      rootSpecs.push(f);
    }
  }

  // Fragment key per member: solver CC index, prev-fragment suffix, or "".
  const prevFragKey = new Map<string, string>(); // member → cc-suffix ("" = unsplit)
  if (!components && prevFrames) {
    for (const pf of prevFrames) {
      const suffix = pf.id.includes("::cc") ? pf.id.slice(pf.id.lastIndexOf("::cc") + 4) : "";
      for (const id of pf.directMemberIds ?? pf.memberIds) prevFragKey.set(id, suffix);
    }
  }
  const ccKeyOf = (id: string): string =>
    components
      ? String(components.get(id) ?? "")
      : prevFragKey.get(id) ?? "";

  interface Frag {
    spec: GroupFrameSpec;
    cc: string;
    depth: number;
    parent?: Frag;
    children: Frag[];
    direct: string[];
  }
  // Build fragment trees per root spec: cc set = union of descendant
  // members' cc keys; a child fragment binds to the same-cc parent fragment.
  const allFrags: Frag[] = [];
  const buildFrags = (
    spec: GroupFrameSpec,
    depth: number,
    parentByCc: Map<string, Frag> | undefined,
  ): Map<string, Frag> => {
    const directByCc = new Map<string, string[]>();
    for (const id of spec.memberBoardIds) {
      if (!positions.has(id)) continue;
      const k = ccKeyOf(id);
      const arr = directByCc.get(k) ?? [];
      arr.push(id);
      directByCc.set(k, arr);
    }
    // Children first (their cc sets decide this spec's fragments too).
    const childFragsByCc = new Map<string, Frag[]>();
    const pending = childSpecs.get(spec.id) ?? [];
    // Two-phase: we need this spec's fragments to exist before binding
    // children — create fragments for every cc seen in direct members OR
    // any descendant. Collect descendant ccs via a pre-scan.
    const descCcs = new Set<string>(directByCc.keys());
    const scan = (sp: GroupFrameSpec): void => {
      for (const id of sp.memberBoardIds) if (positions.has(id)) descCcs.add(ccKeyOf(id));
      for (const c of childSpecs.get(sp.id) ?? []) scan(c);
    };
    for (const c of pending) scan(c);
    const fragsByCc = new Map<string, Frag>();
    for (const cc of [...descCcs].sort()) {
      const frag: Frag = {
        spec,
        cc,
        depth,
        ...(parentByCc?.get(cc) ? { parent: parentByCc.get(cc)! } : {}),
        children: [],
        direct: directByCc.get(cc) ?? [],
      };
      if (frag.parent) frag.parent.children.push(frag);
      fragsByCc.set(cc, frag);
      allFrags.push(frag);
    }
    for (const c of pending) {
      const childByCc = buildFrags(c, depth + 1, fragsByCc);
      for (const [cc, cf] of childByCc) childFragsByCc.set(cc, [...(childFragsByCc.get(cc) ?? []), cf]);
    }
    return fragsByCc;
  };
  for (const rs of rootSpecs) buildFrags(rs, 0, undefined);

  // Prune empty fragments (no direct members and no surviving children).
  const alive = (f: Frag): boolean => f.direct.length > 0 || f.children.some(alive);
  const liveFrags = allFrags.filter(alive);

  // Fragment ids: spec id, + `::cc<k>` only when the SPEC split across ccs.
  const splitOf = new Map<string, number>();
  for (const f of liveFrags) splitOf.set(f.spec.id, (splitOf.get(f.spec.id) ?? 0) + 1);
  const idOf = (f: Frag): string =>
    (splitOf.get(f.spec.id) ?? 0) > 1 ? `${f.spec.id}::cc${f.cc}` : f.spec.id;

  // Rects bottom-up (memoized by object identity).
  const rectMemo = new Map<Frag, { x: number; y: number; w: number; h: number }>();
  const rectOf = (f: Frag): { x: number; y: number; w: number; h: number } => {
    const hit = rectMemo.get(f);
    if (hit) return hit;
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const id of f.direct) {
      const r = positions.get(id)!;
      x0 = Math.min(x0, r.x);
      y0 = Math.min(y0, r.y);
      x1 = Math.max(x1, r.x + r.w);
      y1 = Math.max(y1, r.y + r.h);
    }
    for (const c of f.children) {
      if (!alive(c)) continue;
      const r = rectOf(c);
      x0 = Math.min(x0, r.x);
      y0 = Math.min(y0, r.y);
      x1 = Math.max(x1, r.x + r.w);
      y1 = Math.max(y1, r.y + r.h);
    }
    const r = {
      x: x0 - FRAME_PAD,
      y: y0 - FRAME_PAD - FRAME_LABEL_H,
      w: x1 - x0 + 2 * FRAME_PAD,
      h: y1 - y0 + 2 * FRAME_PAD + FRAME_LABEL_H,
    };
    rectMemo.set(f, r);
    return r;
  };
  const descendants = (f: Frag): string[] => [
    ...f.direct,
    ...f.children.filter(alive).flatMap(descendants),
  ];

  return liveFrags
    .sort((a, b) => a.depth - b.depth || (idOf(a) < idOf(b) ? -1 : 1))
    .map((f) => {
      const r = rectOf(f);
      return {
        id: idOf(f),
        label: f.spec.label,
        x: r.x,
        y: r.y,
        w: r.w,
        h: r.h,
        memberIds: descendants(f),
        ...(f.parent && alive(f.parent) ? { parentId: idOf(f.parent) } : {}),
        depth: f.depth,
        directMemberIds: f.direct,
      };
    });
}

export function placeSpace(
  spaceId: string,
  children: readonly ModuleWithParent[],
  ctx: LayoutContext,
  /** The previous build of THIS space (engine cache carry) — the group-frame
   *  fallback for transient no-solve frames (drag-throttle skip, animation
   *  override), where no solver `components` map exists. Without it a
   *  cross-CC group collapses to one fragment for a frame (flicker). */
  prevSpace?: BoardSpace,
): BoardSpace {
  const spaceParentId =
    spaceId === "root::space"
      ? undefined
      : spaceId.slice(0, -"::space".length);
  const spaceEdges = ctx.edgesByParent.get(spaceParentId) ?? [];

  // Solver input: nodes, directed edges, external ports (direction only).
  // We intentionally run the solver over ALL children, including ones that
  // will be hand-pinned — the solver treats pinned rects as hard anchors so
  // auto-positioned neighbours still relax around them.
  const nodes: StressNode[] = children.map((m) => {
    const { w, h } = resolveNodeSize(extractWidgetSpec(m), m);
    return {
      id: moduleKey(m),
      w,
      h,
      team: getTeam(m),
      domain: getDomain(m),
    };
  });
  const idSet = new Set(nodes.map((n) => n.id));
  const edgesForSolver: StressEdge[] = spaceEdges
    .filter((e) => idSet.has(e.from) && idSet.has(e.to))
    .map((e) => ({ from: e.from, to: e.to }));

  // `group by` frames live on the root space only (frame groups).
  const frameSpecs =
    spaceParentId === undefined && ctx.groupFrames?.length
      ? ctx.groupFrames
      : undefined;
  const framePartition = frameSpecs ? partitionOf(frameSpecs, idSet) : undefined;
  const frameParents = frameSpecs ? parentsOf(frameSpecs) : undefined;

  // Per-item effective ports: real cross-space ports + view boundary stubs
  // (the stub is a port in every sense — wall placement, solver pull, count
  // badge — its peer just isn't on the board).
  const effectivePortsOf = (k: string): readonly ExternalPort[] | undefined => {
    const real = ctx.externalPorts.get(k);
    const stubs = ctx.boundaryPorts?.get(k);
    if (!stubs || stubs.length === 0) return real;
    return real && real.length > 0 ? [...real, ...stubs] : stubs;
  };

  const ports: StressPort[] = [];
  for (const m of children) {
    const xps = effectivePortsOf(moduleKey(m)) ?? [];
    for (const p of xps) {
      ports.push({ nodeId: moduleKey(m), direction: p.direction, peerId: p.peerId });
    }
  }

  const pinned = new Map<string, StressRect>();
  for (const m of children) {
    const override = ctx.placements.get(moduleKey(m));
    if (override) pinned.set(moduleKey(m), override);
  }

  const previousForThisSpace = new Map<string, StressRect>();
  for (const m of children) {
    const prev = ctx.previousLayout.get(moduleKey(m));
    if (prev) previousForThisSpace.set(moduleKey(m), prev);
  }

  const { budget, mode } = chooseSolveParams(
    children.map((m) => moduleKey(m)),
    previousForThisSpace,
    pinned,
    ctx.dragging,
  );

  // Model-edit gesture detection (idle warm recomputes only): does THIS
  // space's geometry differ from the edit baseline? Compare child set,
  // effective sizes, edge multiset, and external ports. On a real delta the
  // pre-edit rects + topology ride along to the solver so the edit acts as
  // a force gesture; identical geometry keeps the maintenance-hold path.
  let editExtras:
    | {
        trimBase: Map<string, StressRect>;
        editBaseline: {
          edges: StressEdge[];
          ports: StressPort[];
          partition?: ReadonlyMap<string, string>;
        };
      }
    | undefined;
  if (ctx.editBase && !ctx.dragging && budget === "incremental") {
    const oldChildren =
      spaceParentId === undefined
        ? ctx.editBase.structure.roots
        : ctx.editBase.structure.byParent.get(spaceParentId) ?? [];
    const oldIds = oldChildren.map((m) => moduleKey(m));
    const oldIdSet = new Set(oldIds);
    let delta =
      oldIds.length !== children.length ||
      nodes.some((n) => !oldIdSet.has(n.id));
    if (!delta) {
      for (const n of nodes) {
        // Effective solver size: a pin's rect wins (mirrors the solver's
        // own size map), so a pinned card never false-positives here.
        const pin = pinned.get(n.id);
        const w = pin?.w ?? n.w;
        const h = pin?.h ?? n.h;
        const old = ctx.editBase.rects.get(n.id);
        if (old && (old.w !== w || old.h !== h)) {
          delta = true;
          break;
        }
      }
    }
    const oldSpaceEdges = (
      ctx.editBase.structure.edgesByParent.get(spaceParentId) ?? []
    ).filter((e) => oldIdSet.has(e.from) && oldIdSet.has(e.to));
    if (!delta) {
      if (oldSpaceEdges.length !== edgesForSolver.length) {
        delta = true;
      } else {
        const count = new Map<string, number>();
        for (const e of oldSpaceEdges) {
          const k = `${e.from}|${e.to}`;
          count.set(k, (count.get(k) ?? 0) + 1);
        }
        for (const e of edgesForSolver) {
          const k = `${e.from}|${e.to}`;
          const c = count.get(k) ?? 0;
          if (c === 0) {
            delta = true;
            break;
          }
          count.set(k, c - 1);
        }
      }
    }
    const oldPorts: StressPort[] = [];
    for (const id of oldIds) {
      for (const p of ctx.editBase.structure.externalPorts.get(id) ?? []) {
        oldPorts.push({ nodeId: id, direction: p.direction, peerId: p.peerId });
      }
    }
    if (!delta) {
      // Boundary stubs are excluded from the comparison: the edit baseline's
      // structure carries only REAL ports, and a same-view no-op recompute
      // must not read the stub overlay as a geometry delta.
      const realPorts: StressPort[] = [];
      for (const m of children) {
        for (const p of ctx.externalPorts.get(moduleKey(m)) ?? []) {
          realPorts.push({ nodeId: moduleKey(m), direction: p.direction, peerId: p.peerId });
        }
      }
      const sig = (ps: readonly StressPort[]) =>
        ps
          .map((p) => `${p.nodeId}|${p.direction}|${p.peerId ?? ""}`)
          .sort()
          .join(";");
      if (sig(realPorts) !== sig(oldPorts)) delta = true;
    }
    // A group-membership change is geometry-invisible (same children, sizes,
    // edges, ports) but MUST re-solve as an edit gesture — the flipped node
    // travels to its new frame. Compare frame membership signatures.
    if (!delta && spaceParentId === undefined) {
      const fsig = (fs?: readonly GroupFrameSpec[]) =>
        (fs ?? [])
          .map((f) => `${f.id}<${f.parentId ?? ""}:${[...f.memberBoardIds].sort().join(",")}`)
          .sort()
          .join(";");
      if (fsig(frameSpecs) !== fsig(ctx.editBase.groupFrames)) delta = true;
    }
    if ((globalThis as { __ARCH_EDIT_DEBUG?: boolean }).__ARCH_EDIT_DEBUG) {
      // eslint-disable-next-line no-console
      console.log(
        `[edit] space=${spaceId} budget=${budget} delta=${delta} oldIds=${oldIds.length} curEdges=${edgesForSolver.length} oldEdges=${oldSpaceEdges.length} rects=${ctx.editBase.rects.size}`,
      );
    }
    if (delta) {
      const trimBase = new Map<string, StressRect>();
      for (const id of oldIds) {
        const r = ctx.editBase.rects.get(id);
        if (r) trimBase.set(id, r);
      }
      if (trimBase.size > 0) {
        editExtras = {
          trimBase,
          editBaseline: {
            edges: oldSpaceEdges.map((e) => ({ from: e.from, to: e.to })),
            ports: oldPorts,
            // Pre-edit forced partition — the calibration world's rules.
            ...(spaceParentId === undefined && ctx.editBase.groupFrames?.length
              ? {
                  partition: partitionOf(ctx.editBase.groupFrames, oldIdSet),
                  ...(parentsOf(ctx.editBase.groupFrames)
                    ? { partitionParents: parentsOf(ctx.editBase.groupFrames)! }
                    : {}),
                }
              : {}),
          },
        };
      }
    }
  }

  const isRoot = spaceId === "root::space";
  // Animation mode for the root space: positions come from an externally-
  // driven generator step. Only take the override path if it covers every
  // current child (guards against topology changes mid-animation — e.g.
  // the model reloads with new modules and the stale override would miss
  // them, causing a crash when we later read basePositions.get(id)!).
  const overrideCoversAll =
    isRoot &&
    ctx.rootPositionsOverride !== undefined &&
    children.every((m) => ctx.rootPositionsOverride!.has(moduleKey(m)));

  // Drag fast-path: a node drag only perturbs the ONE space that contains
  // the node being moved. Every other space — sibling subtrees and the
  // dragged node's own children — has identical inputs frame-to-frame, so
  // re-solving it is pure waste (the solver already freezes pin-free CCs
  // internally; this just skips the whole stressLayout call). We detect
  // "the space the drag is happening in" as the one whose pinned position
  // differs from its warm-start: the dragged node's placement moves every
  // pointer frame, while every other pin sits exactly on its last-rendered
  // spot. Spaces with a full warm-start and no moved pin reuse it verbatim,
  // so output is identical to the gentle 2-iter relax from an already-
  // converged start — minus the per-frame cost.
  const warmCoversAll = previousForThisSpace.size === children.length;
  const aPinMoved = [...pinned].some(([id, r]) => {
    const prev = previousForThisSpace.get(id);
    return !prev || prev.x !== r.x || prev.y !== r.y;
  });
  // Drag relaxation throttle (dogfood, ctx.dragSolveSkip): on skip frames
  // even the MOVED-pin space reuses its warm layout — the dragged node
  // still tracks the pointer exactly (pins override the warm rects below),
  // only the neighbours' relaxation runs at half rate. The release solve is
  // untouched, so the final layout is identical; this halves the solver
  // share of big-board drag frames (memory: ~50% solver / 50% render).
  const reuseWarm =
    ctx.dragging && warmCoversAll && (!aPinMoved || ctx.dragSolveSkip === true);

  let basePositions: Map<string, StressRect>;
  let solvedComponents: ReadonlyMap<string, number> | undefined;
  if (overrideCoversAll) {
    basePositions = new Map();
    for (const m of children) {
      basePositions.set(moduleKey(m), ctx.rootPositionsOverride!.get(moduleKey(m))!);
    }
    // Pinned items still win — they override even the animation snapshot.
    for (const [id, r] of pinned) basePositions.set(id, r);
  } else if (reuseWarm) {
    basePositions = new Map(previousForThisSpace);
    // Pins still win, though by construction they equal the warm-start here.
    for (const [id, r] of pinned) basePositions.set(id, r);
  } else {
    const solved = stressLayout({
      nodes,
      edges: edgesForSolver,
      externalPorts: ports,
      previous: previousForThisSpace,
      pinned,
      budget,
      mode,
      ...(ctx.direction ? { direction: ctx.direction } : {}),
      ...(ctx.trimBase ? { trimBase: ctx.trimBase } : {}),
      ...(framePartition?.size ? { partition: framePartition } : {}),
      ...(framePartition?.size && frameParents ? { partitionParents: frameParents } : {}),
      ...(editExtras ?? {}),
    });
    basePositions = solved.positions;
    solvedComponents = solved.components;
  }

  // Shallow item build — no recursion. Each item that owns grandchildren is
  // flagged `hasSpace`; filling its `.space` is the caller's job (eager
  // buildSpace recurses immediately; the lazy engine recurses to a depth).
  const items: BoardItem[] = children.map((m) => {
    const pos = basePositions.get(moduleKey(m))!;
    const hasSpace = (ctx.byParent.get(moduleKey(m))?.length ?? 0) > 0;
    return {
      id: moduleKey(m),
      // `displayLabel` = an active view's `style X { rename … }` (baked by
      // applyProjection); undefined on the default board → falls back to `name`.
      label: m.displayLabel ?? m.name,
      x: pos.x,
      y: pos.y,
      w: pos.w,
      h: pos.h,
      ...(hasSpace ? { hasSpace: true } : {}),
      ...(m.logicalId ? { logicalId: m.logicalId } : {}),
      externalPorts: effectivePortsOf(moduleKey(m)),
      internalCalls: ctx.internalCalls.get(moduleKey(m)),
      meta: m,
    };
  });

  // `group by` frame rects over the solved member positions (root of a
  // grouped view only). The solver's own CC decomposition drives the
  // (group × CC) fragment split; transient no-solve paths (animation
  // override, drag warm-reuse) fall back to the previous build's fragments.
  const placedFrames = frameSpecs
    ? placeGroupFrames(
        frameSpecs,
        basePositions,
        solvedComponents,
        ctx.prevSpaceById?.get(spaceId)?.groupFrames ?? prevSpace?.groupFrames,
      )
    : undefined;

  // Frame follows contents: tight bounding box of all items plus padding on
  // every side. Supports negative origin — when an item has been hand-dragged
  // past (0, 0), the frame extends left/up to wrap it. Group frames extend
  // the extents too — a frame edge must never clip at the space boundary.
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const it of items) {
    minX = Math.min(minX, it.x);
    minY = Math.min(minY, it.y);
    maxX = Math.max(maxX, it.x + it.w);
    maxY = Math.max(maxY, it.y + it.h);
  }
  for (const f of placedFrames ?? []) {
    minX = Math.min(minX, f.x);
    minY = Math.min(minY, f.y);
    maxX = Math.max(maxX, f.x + f.w);
    maxY = Math.max(maxY, f.y + f.h);
  }
  // Wall-port pills stack vertically off each porting node's centre; a hub with
  // many ports on ONE wall fans a stack TALLER than its own body (11 out-ports
  // ⇒ ~388px). Grow the content bounds to contain that natural stack so the
  // carpet — padded below — gives the pills a breather, instead of
  // `resolvePortPlacements` compressing them floor-to-ceiling (the "carpet
  // doesn't respect ports" bug). Walls split by direction (in→left, out→right),
  // the same default the placement uses.
  const wallOwners = (dir: "in" | "out"): { cy: number; count: number }[] => {
    const os: { cy: number; count: number }[] = [];
    for (const it of items) {
      let c = 0;
      for (const p of it.externalPorts ?? []) if (p.direction === dir) c++;
      if (c > 0) os.push({ cy: it.y + it.h / 2, count: c });
    }
    return os;
  };
  for (const dir of ["in", "out"] as const) {
    const ext = wallStackCentreExtent(wallOwners(dir));
    if (ext) {
      minY = Math.min(minY, ext.min - PORT_H / 2);
      maxY = Math.max(maxY, ext.max + PORT_H / 2);
    }
  }
  const PADDING = 60;
  if (items.length === 0) {
    minX = 0;
    minY = 0;
    maxX = 2 * PADDING;
    maxY = 2 * PADDING;
  }
  // Wall-port pills render INSIDE the walls (`resolvePortPlacements` docks them
  // at PORT_WALL_GAP from the face), so a wall carrying ports needs its own
  // lane of margin or the pills overlap the edge-most items. Reserve PORT_LANE
  // on a side only when a port lands there — `in` ports default to the left
  // wall, `out` ports to the right (the same split placement uses). The pills
  // then sit in this reserved margin; item positions are untouched.
  let reserveLeft = false;
  let reserveRight = false;
  for (const p of ports) {
    if (p.direction === "in") reserveLeft = true;
    else reserveRight = true;
  }
  const leftPad = PADDING + (reserveLeft ? PORT_LANE : 0);
  const rightPad = PADDING + (reserveRight ? PORT_LANE : 0);
  const originX = minX - leftPad;
  const originY = minY - PADDING;
  const spaceW = Math.max(maxX - minX + leftPad + rightPad, 2 * PADDING);
  const spaceH = Math.max(maxY - minY + 2 * PADDING, 2 * PADDING);

  return {
    id: spaceId,
    items,
    originX,
    originY,
    width: spaceW,
    height: spaceH,
    edges: spaceEdges,
    ...(placedFrames && placedFrames.length > 0 ? { groupFrames: placedFrames } : {}),
  };
}

/**
 * Eager recursive build: place this space, then fill every item's nested
 * `.space` by recursing. Carries the drag-frame identity reuse — a node whose
 * solved bounds are byte-identical to last frame's keeps its prior BoardItem
 * (whole nested subtree included), so React's memo + edge-route memo bail out
 * on the unchanged subtree. Used by `buildBoardFromModules` and the animation
 * driver; the lazy `BoardEngine` uses `placeSpace` directly instead.
 */
function buildSpace(
  spaceId: string,
  children: readonly ModuleWithParent[],
  ctx: LayoutContext,
): BoardSpace {
  const placed = placeSpace(spaceId, children, ctx);

  const prevSpace = ctx.dragging ? ctx.prevSpaceById?.get(spaceId) : undefined;
  const prevItemById = prevSpace
    ? new Map(prevSpace.items.map((it) => [it.id, it] as const))
    : undefined;

  const items: BoardItem[] = [];
  let allReused = prevSpace !== undefined;
  for (const it of placed.items) {
    const prevItem = prevItemById?.get(it.id);
    if (
      prevItem &&
      prevItem.x === it.x &&
      prevItem.y === it.y &&
      prevItem.w === it.w &&
      prevItem.h === it.h
    ) {
      items.push(prevItem);
      continue;
    }
    allReused = false;
    if (!it.hasSpace) {
      items.push(it);
      continue;
    }
    const grandchildren = ctx.byParent.get(it.id) ?? [];
    items.push({ ...it, space: buildSpace(`${it.id}::space`, grandchildren, ctx) });
  }

  // Whole-space reuse: if every child kept its prior identity AND the child
  // set is unchanged, the space is byte-for-byte last frame's — return the
  // prior BoardSpace object so its `edges` array (and thus planRoutes) stay
  // referentially stable too and the subtree skips render + re-route wholly.
  if (allReused && prevSpace && prevSpace.items.length === children.length) {
    return prevSpace;
  }

  return { ...placed, items };
}

/**
 * Build the placement-independent topology — index modules by parent, aggregate
 * edges to their LCA spaces, accumulate external ports + internal calls — and
 * return it as a `LayoutStructure`. This is the heavy O(modules + edges) pass.
 * It reads ONLY `modules` + `edges` (never placements / drag / solve output),
 * so the result is stable across drag/animation/idle and reusable by both the
 * caller's global connection index and the board engine. `prepareLayout` wraps
 * this with the cheap placement layer.
 */
/**
 * Topology lookups derived from `parentOf` (id → parent id) + the module list:
 * ancestor chains, lowest-common-ancestor, the ancestor directly under a space,
 * and the qualified (dotted name) path. Extracted so the full build and the
 * incremental patcher share one implementation — no chance of divergence.
 */
export function routingHelpers(
  parentOf: ReadonlyMap<string, string | undefined>,
  moduleList: readonly ModuleWithParent[],
): {
  ancestorChain: (id: string) => string[];
  lcaModule: (a: string, b: string) => string | undefined;
  ancestorUnder: (x: string, stopAt: string | undefined) => string | undefined;
  qualPath: (id: string) => string;
  nameOf: Map<string, string>;
} {
  const ancestorChain = (id: string): string[] => {
    const chain: string[] = [];
    let cur: string | undefined = id;
    while (cur !== undefined) {
      chain.push(cur);
      cur = parentOf.get(cur);
    }
    return chain;
  };
  const lcaModule = (a: string, b: string): string | undefined => {
    const setA = new Set(ancestorChain(a));
    for (const x of ancestorChain(b)) if (setA.has(x)) return x;
    return undefined;
  };
  const ancestorUnder = (x: string, stopAt: string | undefined): string | undefined => {
    let cur: string | undefined = x;
    while (cur !== undefined && parentOf.get(cur) !== stopAt) cur = parentOf.get(cur);
    return cur;
  };
  const nameOf = new Map<string, string>();
  for (const m of moduleList) nameOf.set(moduleKey(m), m.name);
  const qualPath = (id: string): string =>
    ancestorChain(id).reverse().map((i) => nameOf.get(i) ?? i).join(".");
  return { ancestorChain, lcaModule, ancestorUnder, qualPath, nameOf };
}

/**
 * Target-side row anchor for an aggregated edge: when the edge targets a single
 * interface that lives directly on the `to` module, and that module's widget
 * exposes a `rowAnchorFrac` hook, return the vertical fraction of that
 * interface's row. Otherwise undefined (plain centre anchoring). Cross-level
 * edges — where the target interface is nested deeper than `to` — never qualify.
 */
export function makeRowAnchorForEdge(
  qualPath: (id: string) => string,
  moduleById: ReadonlyMap<string, Module>,
): (toId: string, details: readonly ExternalPortDetail[]) => number | undefined {
  return (toId, details) => {
    let only: string | undefined;
    for (const d of details) {
      if (!d.toInterfaceId) return undefined;
      if (only === undefined) only = d.toInterfaceId;
      else if (only !== d.toInterfaceId) return undefined; // ambiguous target
    }
    if (!only) return undefined;
    const dot = only.lastIndexOf(".");
    if (dot < 0) return undefined;
    const ownerPath = only.slice(0, dot);
    const ifaceName = only.slice(dot + 1);
    if (ownerPath !== qualPath(toId)) return undefined; // interface nested deeper
    const m = moduleById.get(toId);
    if (!m) return undefined;
    const spec = extractWidgetSpec(m);
    return widgetTagRowAnchor(spec.tag, ifaceName, {
      typeName: m.typeName,
      interfaces: m.interfaces,
      aspects: m.aspects,
    });
  };
}

/** One aggregated sibling-edge: the routing result before styling is applied. */
export interface AggregatedEdge {
  readonly parent: string | undefined;
  readonly from: string;
  readonly to: string;
  readonly details: readonly ExternalPortDetail[];
  readonly aspectColor?: string;
  /** A `replicated`-mode member→duplicate tether — `BoardEdge.stub`. */
  readonly aspectStub?: boolean;
  /** The active view's edge restyle (`applyProjection` baked it onto the source
   *  `DependencyEdge.widgetStyle` from a `style <edge>`/`style violating <P>`
   *  clause). Undefined on the default board — so this fold is a no-op there. */
  readonly viewStyle?: EdgeWidgetStyleData;
  /** Live-conformance undocumented edge — flows to `BoardEdge.violation`. */
  readonly violation?: boolean;
}

/**
 * Turn one routed `AggregatedEdge` into its final styled `BoardEdge` — resolves
 * the called interfaces, aggregates their widget config, applies the aspect-web
 * stroke, and computes the target row anchor. Shared by the full build and the
 * incremental re-style pass so a patched edge is byte-identical to a rebuilt one.
 */
/**
 * Aggregate the widget config of the target interfaces a set of details call.
 * Shared by edges, ports, and connections so a cross-space segment styles
 * identically to a same-space edge (homogeneous keeps the style, heterogeneous
 * collapses to neutral — `aggregateInterfaceWidget`'s conflict rule).
 */
export function resolveWidgetFromDetails(
  details: readonly ExternalPortDetail[],
  interfaceByPath: ReadonlyMap<string, Interface>,
): ReturnType<typeof aggregateInterfaceWidget> {
  const interfaces: Interface[] = [];
  const seen = new Set<string>();
  for (const d of details) {
    if (!d.toInterfaceId || seen.has(d.toInterfaceId)) continue;
    seen.add(d.toInterfaceId);
    const iface = interfaceByPath.get(d.toInterfaceId);
    if (iface) interfaces.push(iface);
  }
  return aggregateInterfaceWidget(interfaces);
}

/** Paint for a live-conformance UNDOCUMENTED edge/port — amber + dashed, the
 *  same fuchsia-family "drift" language the process reorder ring uses (amber
 *  reserved for the observed-but-unmodeled call). Set at BUILD time so the
 *  existing widgetStyle render path paints it, with no paint-site edits; export
 *  filters violation edges out entirely, so this never reaches a static image. */
const VIOLATION_STYLE = { color: "#f59e0b", dashed: true, width: 2 } as const;

export function styleAggregatedEdge(
  agg: AggregatedEdge,
  interfaceByPath: ReadonlyMap<string, Interface>,
  rowAnchorForEdge: (toId: string, details: readonly ExternalPortDetail[]) => number | undefined,
): BoardEdge {
  const widget = resolveWidgetFromDetails(agg.details, interfaceByPath);
  const aspectStyle = agg.aspectColor
    ? { color: agg.aspectColor, dashed: true, opacity: 0.4, width: 1.5 }
    : undefined;
  // Edges into a synthesized stub interface (v0.6, §4.13) stay PLAIN (solid).
  // The draft signal lives on the dashed stub NODE, not the connection — a
  // dashed edge reads as a special kind of arrow rather than "draft target".
  const toRowFrac = rowAnchorForEdge(agg.to, agg.details);
  return {
    id: `${agg.parent ?? "~"}|${agg.from}→${agg.to}`,
    from: agg.from,
    to: agg.to,
    details: agg.details,
    ...(toRowFrac !== undefined ? { toRowFrac } : {}),
    // An active view's edge restyle (`agg.viewStyle`) is the top lens — it wins
    // over the aspect web + interface widget style, and suppresses the aspect flag.
    ...(aspectStyle && !agg.viewStyle ? { isAspect: true } : {}),
    ...(agg.aspectStub && !agg.viewStyle ? { stub: true } : {}),
    ...(agg.viewStyle
      ? { widgetStyle: agg.viewStyle }
      : agg.violation
        ? { widgetStyle: VIOLATION_STYLE }
        : aspectStyle
          ? { widgetStyle: aspectStyle }
          : widget.style
            ? { widgetStyle: widget.style }
            : {}),
    ...(widget.tag ? { widgetTag: widget.tag } : {}),
    ...(widget.template ? { widgetTemplate: widget.template } : {}),
    ...(widget.props ? { widgetProps: widget.props } : {}),
    ...(agg.violation ? { violation: true } : {}),
  } as BoardEdge;
}

export function buildLayoutStructure(
  modules: readonly Module[],
  edges: readonly DependencyEdge[],
): LayoutStructure {
  const moduleList = modules as readonly ModuleWithParent[];

  // Index by parent
  const byParent = new Map<string, ModuleWithParent[]>();
  const roots: ModuleWithParent[] = [];
  for (const m of moduleList) {
    if (m.parentId) {
      const arr = byParent.get(m.parentId) ?? [];
      arr.push(m);
      byParent.set(m.parentId, arr);
    } else {
      roots.push(m);
    }
  }

  // Build a path-keyed lookup of every interface so per-edge widget
  // styling can find the targets fast. Path is the interface's full
  // qualified name (`Orders.Create`), matching `DependencyEdge.toInterfacePath`.
  const interfaceByPath = buildInterfaceIndex(moduleList);

  // Route each edge to its lowest common ancestor space. Cross-level edges
  // aggregate: e.g. an edge from `Platform.AuthService` to top-level `Payments`
  // shows up at root level as `Platform → Payments`. An edge between two items
  // inside the same parent shows up inside that parent's space.
  const edgesByParent = new Map<string | undefined, BoardEdge[]>();
  const pushEdge = (parent: string | undefined, edge: BoardEdge) => {
    const arr = edgesByParent.get(parent) ?? [];
    arr.push(edge);
    edgesByParent.set(parent, arr);
  };

  const parentOf = new Map<string, string | undefined>();
  for (const m of moduleList) parentOf.set(moduleKey(m), m.parentId);

  const { lcaModule, ancestorUnder, qualPath, nameOf } = routingHelpers(parentOf, moduleList);

  const moduleById = new Map<string, Module>();
  for (const m of moduleList) moduleById.set(moduleKey(m), m);

  const rowAnchorForEdge = makeRowAnchorForEdge(qualPath, moduleById);

  // For each owning module M (an ancestor that's a direct child of some
  // space), accumulate ports keyed by PEER so multiple raw edges aggregate into
  // a single labelled port.
  //
  // Peer, not (direction, peer): a port stands for a RELATIONSHIP with something
  // outside, and a relationship read from the other end is the same
  // relationship. Keying on direction split every mutual peer into two pills on
  // opposite walls, and nothing said they belonged together.
  const portAcc = new Map<string, Map<string, ExternalPort>>();
  const addPort = (
    ownerId: string,
    direction: "out" | "in",
    peerId: string,
    detail: ExternalPortDetail,
    violation = false,
  ) => {
    let perOwner = portAcc.get(ownerId);
    if (!perOwner) {
      perOwner = new Map();
      portAcc.set(ownerId, perOwner);
    }
    // A violation (undocumented) port aggregates SEPARATELY from a modeled port
    // to the same peer — the `v` discriminator keeps the amber stub distinct and
    // never merges telemetry drift into a real cross-space port. Orthogonal to
    // direction: merging the two ways a peer is called must not also merge an
    // observed-but-unmodeled call into the modeled one.
    const key = `${peerId}${violation ? "|v" : ""}`;
    let port = perOwner.get(key);
    if (!port) {
      port = {
        // `|port|` rather than a bare join so the address is self-describing and
        // parseable — the inspector resolves a clicked pill through it. No
        // direction in it: the id is minted on the first crossing seen and never
        // revised, so a direction here would start lying the moment the peer
        // answers back.
        id: `${ownerId}|port|${key}`,
        direction,
        peerLabel: nameOf.get(peerId) ?? peerId,
        peerId,
        details: [detail],
        ...(violation ? { violation: true, widgetStyle: VIOLATION_STYLE } : {}),
      };
      perOwner.set(key, port);
    } else {
      (port.details as ExternalPortDetail[]).push(detail);
      if (port.direction !== direction) {
        (port as { direction: PortDirection }).direction = "inout";
      }
    }
  };

  const hasChildren = (id: string): boolean => (byParent.get(id)?.length ?? 0) > 0;

  // Aggregated sibling-edges by (parentSpace, fromItem, toItem)
  const edgeAcc = new Map<
    string,
    { parent: string | undefined; from: string; to: string; details: ExternalPortDetail[]; aspectColor?: string; aspectStub?: boolean; viewStyle?: EdgeWidgetStyleData; violation?: boolean }
  >();

  const internalCalls = new Map<string, ExternalPortDetail[]>();
  const pushInternal = (itemId: string, detail: ExternalPortDetail) => {
    const arr = internalCalls.get(itemId) ?? [];
    arr.push(detail);
    internalCalls.set(itemId, arr);
  };

  // First-class connection index: every non-self call, grouped by its deepest
  // (source→target) endpoints. The same detail objects also live on the edge +
  // descent ports, so a rendered piece maps back to its connection(s) by id.
  const connections = new Map<string, Connection>();
  const upsertConnection = (d: ExternalPortDetail) => {
    if (d.fromId === d.toId) return; // pure self-call — badge only, no connection
    const id = connectionIdFor(d.fromId, d.toId);
    const existing = connections.get(id);
    if (existing) (existing.details as ExternalPortDetail[]).push(d);
    else connections.set(id, { id, sourceId: d.fromId, targetId: d.toId, details: [d] });
  };

  for (const e of edges) {
    const eFrom = edgeFromKey(e);
    const eTo = edgeToKey(e);
    const eToInterface = e.toInterfacePath.join(".");
    const lca = lcaModule(eFrom, eTo);
    const spaceParent = lca;
    const fromAnc = ancestorUnder(eFrom, spaceParent);
    const toAnc = ancestorUnder(eTo, spaceParent);

    const detail: ExternalPortDetail = {
      fromId: eFrom,
      toId: eTo,
      fromPath: qualPath(eFrom),
      toPath: qualPath(eTo),
      fromInterfaceId: undefined,
      toInterfaceId: eToInterface,
      processId: e.processId,
      stepIndex: e.stepIndex ?? 0,
    };
    upsertConnection(detail);

    // Ancestor/descendant edge: one endpoint IS the LCA space itself, so it has
    // no sibling node inside that space (you can't draw a node→node edge when
    // one "node" is the space wall). Previously these dropped silently. Surface
    // them instead as:
    //   • an internal-activity badge on the space owner — visible from ABOVE,
    //     where the owner is just an item among its siblings;
    //   • a wall port on every ancestor of the descendant endpoint up to (not
    //     including) the owner — visible INSIDE, so each level shows the call
    //     crossing its boundary toward the owner.
    // Covers parent→descendant (`A > A.B.c`), descendant→parent, and the pure
    // self-edge `A > A` (both undefined → badge only).
    if (fromAnc === undefined || toAnc === undefined) {
      // spaceParent === lca is always defined here: one endpoint equalling its
      // own ancestorUnder result means that endpoint IS the lca.
      if (spaceParent === undefined) continue;
      pushInternal(spaceParent, detail);
      if (fromAnc === undefined && toAnc !== undefined) {
        // parent → descendant: incoming call on the descendant branch
        let cur: string | undefined = eTo;
        while (cur !== undefined && cur !== spaceParent) {
          addPort(cur, "in", spaceParent, detail, e.violation);
          cur = parentOf.get(cur);
        }
      } else if (toAnc === undefined && fromAnc !== undefined) {
        // descendant → parent: outgoing call on the descendant branch
        let cur: string | undefined = eFrom;
        while (cur !== undefined && cur !== spaceParent) {
          addPort(cur, "out", spaceParent, detail, e.violation);
          cur = parentOf.get(cur);
        }
      }
      continue;
    }

    if (fromAnc === toAnc) {
      pushInternal(fromAnc, detail);
      continue;
    }

    // 1. Aggregated sibling edge at the LCA space level. A violation
    //    (undocumented) edge buckets SEPARATELY (the `|v` suffix) so it never
    //    merges into a modeled edge that happens to share the same ancestor pair.
    const edgeKey = `${spaceParent ?? "~"}|${fromAnc}→${toAnc}${e.violation ? "|v" : ""}`;
    const agg = edgeAcc.get(edgeKey);
    if (agg) {
      agg.details.push(detail);
      if (!agg.aspectColor && e.source === "aspect") agg.aspectColor = e.aspectColor;
      if (!agg.aspectStub && e.aspectStub) agg.aspectStub = true;
      if (!agg.viewStyle && e.widgetStyle) agg.viewStyle = e.widgetStyle;
    } else {
      edgeAcc.set(edgeKey, {
        parent: spaceParent,
        from: fromAnc,
        to: toAnc,
        details: [detail],
        aspectColor: e.source === "aspect" ? e.aspectColor : undefined,
        ...(e.aspectStub ? { aspectStub: true } : {}),
        viewStyle: e.widgetStyle,
        ...(e.violation ? { violation: true } : {}),
      });
    }

    // 2. External ports on every deeper ancestor of fromAnc and toAnc —
    //    so when you zoom INTO `fromAnc`, its contents still show their
    //    outgoing connection to `toAnc`, as a labelled port on the wall.
    //    We walk the ancestor chain of `from` from `e.from` up to (but not
    //    including) `fromAnc`, and add an OUT port on each ancestor module
    //    targeting `toAnc`.
    let cur: string | undefined = eFrom;
    while (cur !== undefined && cur !== fromAnc) {
      addPort(cur, "out", toAnc, detail, e.violation);
      cur = parentOf.get(cur);
    }
    cur = eTo;
    while (cur !== undefined && cur !== toAnc) {
      addPort(cur, "in", fromAnc, detail, e.violation);
      cur = parentOf.get(cur);
    }
    // NB: a connection whose endpoint IS the container (eFrom===fromAnc /
    // eTo===toAnc) gets NO interior ghost. That call terminates on the container
    // itself, not on anything in its subspace — it's already shown by the edge
    // docking on the container's wall from the parent space. Ports inside a
    // subspace are reserved for connections to its CONTENTS (the descent ports
    // above), so zooming in reveals only what actually crosses into the interior.
  }

  for (const agg of edgeAcc.values()) {
    pushEdge(agg.parent, styleAggregatedEdge(agg, interfaceByPath, rowAnchorForEdge));
  }

  // Style each port + connection from its target interface — same aggregation as
  // edges, so a cross-space segment draws like a same-space edge.
  const styledPort = (p: ExternalPort): ExternalPort => {
    const w = resolveWidgetFromDetails(p.details, interfaceByPath);
    if (!w.style && !w.tag) return p;
    return { ...p, ...(w.style ? { widgetStyle: w.style } : {}), ...(w.tag ? { widgetTag: w.tag } : {}) };
  };
  const externalPorts = new Map<string, ExternalPort[]>();
  for (const [ownerId, perOwner] of portAcc) {
    externalPorts.set(ownerId, [...perOwner.values()].map(styledPort));
  }
  for (const [id, c] of connections) {
    const w = resolveWidgetFromDetails(c.details, interfaceByPath);
    if (w.style || w.tag) {
      connections.set(id, { ...c, ...(w.style ? { widgetStyle: w.style } : {}), ...(w.tag ? { widgetTag: w.tag } : {}) });
    }
  }

  return { byParent, edgesByParent, externalPorts, internalCalls, connections, roots };
}

/**
 * Run the global preparation phase: build the placement-independent
 * `LayoutStructure` (or reuse one passed in), then layer on the cheap placement
 * fields (pins, warm-start, drag/animation state) to assemble a reusable
 * `LayoutContext`. `buildBoardFromModules` (eager) and the lazy `BoardEngine`
 * both call it once, then solve spaces on top of the returned context.
 *
 * Pass `structure` to skip the O(modules + edges) topology build when the caller
 * already has it (e.g. a global connection index reused across edits) — it MUST
 * be built from the same `modules` + `edges`.
 */
export function prepareLayout(
  modules: readonly Module[],
  edges: readonly DependencyEdge[],
  placements: PlacementOverrides = new Map(),
  previousLayout: PreviousLayout = new Map(),
  /**
   * When true, the solver runs the "drag" budget: a handful of gentle
   * iterations per call with a low learning rate, so neighbours nudge
   * slightly each pointer-move frame instead of snapping. Set true while
   * the user is actively dragging a node.
   */
  dragging: boolean = false,
  /**
   * When set, root-space items use these positions verbatim (no solve).
   * The animation driver runs the step generator externally and publishes
   * the current snapshot here each frame. Nested spaces still solve
   * synchronously.
   */
  rootPositionsOverride?: ReadonlyMap<string, StressRect>,
  /**
   * Previous build's root, for drag-frame identity reuse (see
   * `LayoutContext.prevSpaceById`). Only honoured while `dragging`.
   */
  previousRoot?: BoardSpace,
  /**
   * Precomputed topology for these exact `modules` + `edges`. When supplied,
   * the heavy build is skipped and this structure is reused verbatim.
   */
  structure?: LayoutStructure,
  /**
   * Screen direction of the dependency flow (default "LR"). Applied by the
   * solver as a boundary transform for EVERY space this context solves.
   */
  direction?: FlowDirection,
  /** Drag-gesture baseline layout (see LayoutContext.trimBase). */
  trimBase?: ReadonlyMap<string, { x: number; y: number; w: number; h: number }>,
  /** Model-edit baseline (see LayoutContext.editBase). */
  editBase?: LayoutContext["editBase"],
  /** `group by` frames for the root space (see LayoutContext.groupFrames). */
  groupFrames?: readonly GroupFrameSpec[],
  /** View boundary stubs by board id (see LayoutContext.boundaryPorts). */
  boundaryPorts?: ReadonlyMap<string, readonly ExternalPort[]>,
  /** Drag relaxation throttle (see LayoutContext.dragSolveSkip). */
  dragSolveSkip?: boolean,
): { ctx: LayoutContext; roots: readonly ModuleWithParent[] } {
  const { byParent, edgesByParent, externalPorts, internalCalls, connections, roots } =
    structure ?? buildLayoutStructure(modules, edges);

  // Index the previous tree by space id for drag-frame identity reuse.
  let prevSpaceById: Map<string, BoardSpace> | undefined;
  if (dragging && previousRoot) {
    prevSpaceById = new Map();
    const walk = (s: BoardSpace) => {
      prevSpaceById!.set(s.id, s);
      for (const it of s.items) if (it.space) walk(it.space);
    };
    walk(previousRoot);
  }

  const ctx: LayoutContext = {
    byParent,
    edgesByParent,
    externalPorts,
    internalCalls,
    connections,
    placements,
    previousLayout,
    dragging,
    rootPositionsOverride,
    prevSpaceById,
    ...(direction ? { direction } : {}),
    ...(trimBase ? { trimBase } : {}),
    ...(editBase ? { editBase } : {}),
    ...(groupFrames && groupFrames.length > 0 ? { groupFrames } : {}),
    ...(boundaryPorts && boundaryPorts.size > 0 ? { boundaryPorts } : {}),
    ...(dragSolveSkip ? { dragSolveSkip } : {}),
  };
  return { ctx, roots };
}

/**
 * Build a board tree from a flat module list (with parentId refs) and
 * dependency edges. Edges are routed to the lowest space that contains both
 * endpoints (their LCA). Eager: solves every space, including never-opened
 * subspaces. For large nested models prefer the lazy `BoardEngine`
 * (`createBoardEngine`), which solves spaces on demand.
 *
 * The optional `previousLayout` argument is a warm-start: node positions from
 * a previous build, keyed by item id. Pass an empty map (or omit) for a cold
 * solve.
 */
export function buildBoardFromModules(
  modules: readonly Module[],
  edges: readonly DependencyEdge[],
  placements: PlacementOverrides = new Map(),
  previousLayout: PreviousLayout = new Map(),
  dragging: boolean = false,
  rootPositionsOverride?: ReadonlyMap<string, StressRect>,
  previousRoot?: BoardSpace,
  direction?: FlowDirection,
): BoardSpace {
  const { ctx, roots } = prepareLayout(
    modules,
    edges,
    placements,
    previousLayout,
    dragging,
    rootPositionsOverride,
    previousRoot,
    undefined,
    direction,
  );
  return buildSpace("root::space", roots, ctx);
}

/**
 * Build a flat lookup of every interface keyed by its full qualified
 * path (`Top.Sub.Service.Create`). Walks the entire module tree
 * recursively, including nested children. Interface paths follow the
 * same dotted-name convention DependencyEdge.toInterfacePath uses, so
 * the layout adapter can resolve a call's target interface in O(1).
 */
export function buildInterfaceIndex(modules: readonly Module[]): Map<string, Interface> {
  const out = new Map<string, Interface>();
  const visit = (m: Module, prefix: string) => {
    const here = prefix ? `${prefix}.${m.name}` : m.name;
    for (const i of m.interfaces) {
      out.set(`${here}.${i.name}`, i);
    }
    for (const child of m.children) visit(child, here);
  };
  for (const m of modules) {
    if (!(m as ModuleWithParent).parentId) visit(m, "");
  }
  // Modules in the flat list with parentIds are children of others
  // already walked by `visit` above — no need to revisit. The flat
  // view in `modules` is just an iteration aid; the canonical tree
  // is `m.children`.
  return out;
}

/**
 * Walk a computed board tree and extract every item's rect into a flat map,
 * keyed by item id. Suitable for threading back in as `previousLayout` on
 * the next build — gives the solver a warm-start so small edits don't
 * reshuffle the world.
 */
export function snapshotLayout(root: BoardSpace): Map<string, { x: number; y: number; w: number; h: number }> {
  const out = new Map<string, { x: number; y: number; w: number; h: number }>();
  const walk = (space: BoardSpace) => {
    for (const item of space.items) {
      out.set(item.id, { x: item.x, y: item.y, w: item.w, h: item.h });
      if (item.space) walk(item.space);
    }
  };
  walk(root);
  return out;
}

/**
 * Build the `StressInput` that the root-level stress solver would receive.
 * Exposed so the animation driver can drive the step generator externally
 * with exactly the same inputs the sync path uses. Uses the same edge/
 * port aggregation logic that buildBoardFromModules does internally.
 */
export function prepareRootStressInput(
  modules: readonly Module[],
  edges: readonly DependencyEdge[],
  placements: PlacementOverrides = new Map(),
  previousLayout: PreviousLayout = new Map(),
  direction?: FlowDirection,
  /** Forced partition (frame groups) — passed through verbatim so the
   *  animation driver solves under EXACTLY the sync path's assignment;
   *  omitting it under a grouped view would break anim/sync equality. */
  partition?: ReadonlyMap<string, string>,
  /** Nested `group by` parents map — same pass-through contract. */
  partitionParents?: ReadonlyMap<string, string>,
): {
  nodes: StressNode[];
  edges: StressEdge[];
  externalPorts: StressPort[];
  pinned: Map<string, StressRect>;
  previous: Map<string, StressRect>;
  budget: "drag" | "incremental" | "full";
  mode: "float" | "sand";
  direction?: FlowDirection;
  partition?: ReadonlyMap<string, string>;
  partitionParents?: ReadonlyMap<string, string>;
} {
  // Build the board once to get correct aggregated edges and root-level
  // ports — the exact same ones buildBoardFromModules produces. We ignore
  // positions; we only want the graph structure that stressLayout sees.
  const built = buildBoardFromModules(modules, edges, placements, previousLayout);
  const rootNodes: StressNode[] = built.items.map((it) => {
    const meta = it.meta as Module | undefined;
    const { w, h } = meta ? resolveNodeSize(extractWidgetSpec(meta), meta) : { w: 0, h: 0 };
    return {
      id: it.id,
      w,
      h,
      team: meta ? getTeam(meta) : undefined,
      domain: meta ? getDomain(meta) : undefined,
    };
  });
  const idSet = new Set(rootNodes.map((n) => n.id));
  const rootEdges: StressEdge[] = (built.edges ?? [])
    .filter((e) => idSet.has(e.from) && idSet.has(e.to))
    .map((e) => ({ from: e.from, to: e.to }));
  const ports: StressPort[] = [];
  for (const it of built.items) {
    for (const p of it.externalPorts ?? []) {
      // peerId included — it drives port-peer component unions; dropping it
      // would give the animation path a different CC decomposition than the
      // sync path.
      ports.push({ nodeId: it.id, direction: p.direction, peerId: p.peerId });
    }
  }
  const pinned = new Map<string, StressRect>();
  for (const [id, r] of placements) {
    if (idSet.has(id)) pinned.set(id, r);
  }
  const previous = new Map<string, StressRect>();
  for (const [id, r] of previousLayout) {
    if (idSet.has(id)) previous.set(id, r);
  }
  // Same budget/mode decision the sync path (placeSpace) makes — the
  // animation driver must feed the generator EXACTLY the inputs a sync
  // solve of the root space would use, or anim and sync diverge.
  const params = chooseSolveParams(
    rootNodes.map((n) => n.id),
    previous,
    pinned,
    false,
  );
  return {
    nodes: rootNodes,
    edges: rootEdges,
    externalPorts: ports,
    pinned,
    previous,
    budget: params.budget,
    mode: params.mode,
    ...(direction ? { direction } : {}),
    ...(partition ? { partition } : {}),
    ...(partitionParents ? { partitionParents } : {}),
  };
}
